import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Package, CalendarDays, CreditCard, FileText, LogOut, Settings, Home, Archive, ExternalLink, Menu, X, HelpCircle } from 'lucide-react';
import EmailVerificationBanner from './EmailVerificationBanner';
import LanguageSelector from './LanguageSelector';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Layout = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('operator_name');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const navItems = [
    { path: '/app/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/app/sales', icon: Package, label: t('nav.sales') },
    { path: '/app/bookings', icon: CalendarDays, label: t('nav.bookings') },
    { path: '/app/expenses', icon: CreditCard, label: t('nav.expenses') },
    { path: '/app/documents', icon: FileText, label: t('nav.documents') },
    { path: '/app/help', icon: HelpCircle, label: t('nav.help') },
    { path: '/app/archived', icon: Archive, label: t('nav.archive') },
    { path: '/app/settings', icon: Settings, label: t('nav.settings') },
  ];

  const operatorName = localStorage.getItem('operator_name') || 'Tour Operator';

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const NavContent = () => (
    <>
      <div className="p-4 border-b border-border">
        <div 
          className="cursor-pointer hover:opacity-80 transition-opacity" 
          data-testid="app-logo"
          onClick={() => handleNavigation('/')}
        >
          <img 
            src="/tourpilot-logo.png" 
            alt="TourPilot.biz" 
            className="h-12 md:h-14 max-w-[180px] md:max-w-[220px] object-contain"
          />
          <p className="text-[10px] text-muted-foreground mt-1 text-left leading-tight max-w-[180px]">{t('landing.tagline')}</p>
        </div>
      </div>
      
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {/* Home Button */}
        <button
          onClick={() => handleNavigation('/')}
          data-testid="nav-home"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors text-muted-foreground hover:bg-muted hover:text-foreground mb-2 border border-dashed border-border"
        >
          <Home className="w-5 h-5" />
          <span>{t('nav.home')}</span>
          <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
        </button>
        
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-border">
        {/* Language Selector */}
        <div className="mb-4 px-2">
          <LanguageSelector className="w-full" />
        </div>
        
        <div className="flex items-center gap-3 mb-4 px-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
            {operatorName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{operatorName}</p>
            <p className="text-xs text-muted-foreground">Operator</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>{t('nav.logout')}</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 flex items-center justify-between p-4 bg-card border-b border-border">
        <div 
          className="cursor-pointer" 
          onClick={() => navigate('/')}
        >
          <img 
            src="/tourpilot-logo.png" 
            alt="TourPilot.biz" 
            className="h-10 max-w-[140px] object-contain"
          />
        </div>
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
            <NavContent />
          </SheetContent>
        </Sheet>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-border bg-card h-screen sticky top-0">
          <NavContent />
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <EmailVerificationBanner />
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
